import { RootState } from 'store/store';
import { useAppStore } from 'store/store-hooks';
import { roundNumber, selectSbClipPlaybackParams } from 'components/app/_helpers/_app.helper';
import { useAudioTrackContext } from 'components/app/audio-track/_mediator/audio-track.provider';
import { Clip } from 'components/app/editor/_mediator/editor.slice';
import { getCellMarkersTime } from 'components/app/editor/_mediator/editor-markers/editor-markers.helper';

export type ResizePosition = 'left' | 'right';

export const useCellResizePayload = (clip: Clip | null, cell: Index) => {
  const { wsGetCursorPosition, leftOffset } = useAudioTrackContext();
  const store = useAppStore();

  return (position: ResizePosition) => {
    const { duration, markers, playbackMap } = selectData(store.getState());
    if (!clip || !markers || !duration) return;

    const { startTime, endTime, rate } = selectSbClipPlaybackParams(clip.clipId, playbackMap);
    const { leftTime, rightTime } = getCellMarkersTime(cell, markers, duration);
    const isLeft = position === 'left';

    const getClipDurCb = () => {
      const left = (wsTime: Seconds) => rightTime - wsTime;
      const right = (wsTime: Seconds) => wsTime - leftTime;
      return isLeft ? left : right;
    };

    const getClipTimeCb = () => {
      const left = (cellDur: Seconds) => endTime - cellDur * rate;
      const right = (cellDur: Seconds) => startTime + cellDur * rate;
      return isLeft ? left : right;
    };

    const getClipDur: (wsTime: Seconds) => Seconds = getClipDurCb();
    const getClipTime: (cellDur: Seconds) => Seconds = getClipTimeCb();

    const getWsTimeData = (wsTime: Seconds) => {
      const cellResizeDur = getClipDur(wsTime);
      const clipTime = roundNumber(getClipTime(cellResizeDur));
      return { cellResizeDur, clipTime };
    };

    const getWsPlayerEndTime = (clipTime: Seconds) => {
      const cellDur = (isLeft ? endTime : clipTime - startTime) / rate;
      return isLeft ? rightTime - cellDur : leftTime + cellDur;
    };

    const getCursorPosition = (wsTime: Seconds) => {
      return wsGetCursorPosition(wsTime / duration) + leftOffset.current;
    };

    return { getWsPlayerEndTime, getWsTimeData, getCursorPosition };
  };
};

/* S E L E C T O R */

const selectData = (state: RootState) => {
  const { markers } = state.editor;
  const { duration } = state.audio;
  const { sbClipPlaybackMap: playbackMap } = state.sb;
  return { duration, playbackMap, markers };
};
